import { useState } from "react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useUpdateCustomer, type CustomerPatchableField } from "@/hooks/use-customers";
import { hasMinRole } from "@/lib/constants";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * Redigerbar rad for kundeinfo.
 *
 * VIB-2531: lagringen gikk tidligere via `useUpdateField` rett til
 * `customers`-tabellen. `import-customers` (pg_cron 04:30) gjør full upsert fra
 * PowerOffice, så endringen ble stille overskrevet neste morgen — en PL rettet
 * kundeinfo kl 14, det så ut til å lykkes, og var borte dagen etter.
 *
 * Nå går den via `useUpdateCustomer` → `update-po-customer`, som sender
 * endringen til PowerOffice FØRST og lagrer lokalt kun hvis PO tok imot. Da kan
 * Supabase aldri ha en verdi PO ikke kjenner, og nattsyncen blir harmløs.
 *
 * Lagringen er bevisst ikke optimistisk, og feltet lukkes først når PO har
 * kvittert. Det gir et lite hakk, og det er riktig: da vet du at endringen
 * faktisk nådde regnskapssystemet.
 *
 * Rolle-gatingen her er en GROVSORTERING for å slippe å vise en knapp som
 * garantert feiler. Den ekte gaten ligger i edge function-en (alle aktive
 * ansatte unntatt møtebooker, `canEditCustomers`) og speiler RLS-policyen
 * `customers_employee_update`. Her stenger vi ute `external` (regnskap/revisor)
 * og `skjerm`; en møtebooker blir avvist av serveren.
 */
export function EditableCustomerRow({
  label,
  value,
  field,
  customerId,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  field: CustomerPatchableField;
  customerId: string;
  /** Kort forklaring under raden, f.eks. hva faktura-e-post styrer. */
  hint?: string;
}) {
  const { activeUser } = useImpersonation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? "");
  const updateCustomer = useUpdateCustomer();
  const isSaving = updateCustomer.isPending;

  // `external` (regnskap/revisor) skal aldri endre kundedata.
  const canEdit = hasMinRole(activeUser?.role ?? "external", "user");

  const handleSave = () => {
    if (editValue === (value ?? "")) { setIsEditing(false); return; }
    updateCustomer.mutate(
      { id: customerId, field, value: editValue === "" ? null : editValue },
      {
        onSuccess: () => setIsEditing(false),
        // Avvist av PO (eller manglende rettighet): rull tilbake til lagret
        // verdi, så feltet aldri viser noe som ikke står i PowerOffice.
        onError: () => setEditValue(value ?? ""),
      },
    );
  };

  if (!isEditing) {
    return (
      <div className="space-y-0.5">
        <div
          className={
            canEdit
              ? "flex justify-between items-center group cursor-pointer hover:bg-muted/40 px-2 py-1 rounded transition-colors"
              : "flex justify-between items-center px-2 py-1"
          }
          onClick={canEdit ? () => setIsEditing(true) : undefined}
        >
          <span className="text-muted-foreground">{label}</span>
          <div className="flex items-center gap-2">
            <span className="font-medium text-right">{value ?? "—"}</span>
            {canEdit && (
              <SrIcon name="pencil" className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity" />
            )}
          </div>
        </div>
        {hint && <p className="text-xs text-muted-foreground px-2">{hint}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      <div className="flex justify-between items-center gap-2 bg-muted/50 px-2 py-1 rounded">
        <span className="text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          <input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") { setIsEditing(false); setEditValue(value ?? ""); } }}
            autoFocus
            disabled={isSaving}
            aria-label={label}
            className="px-2 py-1 text-sm border rounded bg-background w-48"
          />
          {isSaving && <SrIcon name="loader2" className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
      </div>
      {isSaving && (
        <p className="text-xs text-muted-foreground px-2">Lagrer i PowerOffice…</p>
      )}
    </div>
  );
}

export default EditableCustomerRow;
